"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Sparkles, Bot, ChevronRight } from "lucide-react";
import Link from "next/link";
import { SectionReveal } from "@/components/common/section-reveal";
import { chatQueryLink } from "@/lib/url";

const QUESTIONS = [
  {
    q: "보톡스 맞으면 아파요?",
    a: "보톡스는 매우 가는 바늘을 사용하며, 대부분 경미한 따끔함 정도입니다.\n\n📊 논문 2,450편 분석:\n• 통증 보고 91%가 일시적\n• 심각도: 경미(mild)\n• 마취 크림 도포 시 더 편안",
  },
  {
    q: "필러 효과 얼마나 가요?",
    a: "필러 종류에 따라 다릅니다.\n\n⏱ 히알루론산 필러: 6~18개월\n⏱ 콜라겐 자극 필러: 1~2년\n📋 3,541편 논문 기반 분석\n\n입술·볼·팔자 부위별로 지속 기간이 달라요.",
  },
  {
    q: "레이저토닝 몇 회 해야해요?",
    a: "레이저토닝은 보통 반복 시술이 필요합니다.\n\n🔄 권장 횟수: 5~10회\n📅 시술 간격: 1~2주\n📊 임상 연구 기반 중앙값\n\n색소 정도에 따라 개인차가 있습니다.",
  },
];

export function AIPreview() {
  const [selected, setSelected] = useState(0);
  const router = useRouter();

  return (
    <SectionReveal>
      <section className="px-5 py-8">
        <h2 className="mb-2 flex items-center gap-2.5 text-xl font-bold tracking-tight">
          <span className="h-5 w-1 rounded-sm bg-gradient-to-b from-violet-500 to-violet-700" />
          AI에게 물어보세요
        </h2>
        <p className="text-muted-foreground mb-5 ml-[18px] text-sm">
          임상 데이터 기반으로 시술 궁금증을 해결해드려요
        </p>

        {/* 질문 칩 */}
        <div className="scrollbar-none -mx-5 flex gap-2 overflow-x-auto px-5 pb-3">
          {QUESTIONS.map((item, i) => (
            <button
              key={i}
              onClick={() => {
                if (selected === i) {
                  router.push(chatQueryLink(item.q));
                } else {
                  setSelected(i);
                }
              }}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium transition-all active:scale-95 ${
                selected === i
                  ? "bg-violet-600 text-white shadow-md shadow-violet-600/15"
                  : "border border-gray-200 bg-white text-gray-600 hover:border-violet-300"
              }`}
            >
              {item.q}
            </button>
          ))}
        </div>

        {/* 답변 카드 */}
        <div
          onClick={() => router.push(chatQueryLink(QUESTIONS[selected].q))}
          className="mt-3 cursor-pointer overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all hover:border-violet-200 hover:shadow-md"
        >
          {/* 답변 헤더 */}
          <div className="flex items-center gap-2.5 border-b border-gray-50 px-5 py-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-violet-700">
              <Sparkles className="h-3.5 w-3.5 text-white" strokeWidth={2} />
            </div>
            <span className="text-sm font-bold text-gray-900">skinagent AI</span>
          </div>

          {/* 질문 */}
          <div className="px-5 pt-4">
            <div className="inline-block rounded-2xl rounded-br-md bg-violet-600 px-4 py-2.5 text-sm font-medium text-white">
              {QUESTIONS[selected].q}
            </div>
          </div>

          {/* 답변 */}
          <div className="px-5 py-4">
            <div className="flex gap-2.5">
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-50">
                <Bot className="h-3 w-3 text-violet-600" strokeWidth={2} />
              </div>
              <div className="rounded-2xl rounded-bl-md border border-gray-100 bg-gray-50 px-4 py-3 text-sm leading-relaxed whitespace-pre-line text-gray-700">
                {QUESTIONS[selected].a}
              </div>
            </div>
          </div>

          {/* 하단 */}
          <div className="flex items-center justify-between border-t border-gray-50 px-5 py-3">
            <p className="text-xs text-gray-300">PubMed 논문 기반 · 의료 조언이 아닙니다</p>
            <Link
              href="/chat"
              className="flex items-center gap-1 text-xs font-semibold text-violet-600 transition-colors hover:text-violet-700"
            >
              대화 시작
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </SectionReveal>
  );
}
